import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Pencil, Trash2 } from 'lucide-react';
import { API, formatErr, notifyErr } from './shared';
import EditLogDialog from './EditLogDialog';

const LogsDialog = ({ item, onClose, authHeader, onItemChanged }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLog, setEditingLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inventory/${item.id}/logs`, { headers: authHeader() });
      setLogs(data);
    } finally {
      setLoading(false);
    }
  }, [item.id, authHeader]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold" data-testid="logs-dialog-title">Activity Log</h2>
            <p className="text-xs text-slate-400 mt-0.5">{item.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="logs-dialog-close"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="text-slate-400 text-sm py-8 text-center"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
          {!loading && logs.length === 0 && <div className="text-slate-400 text-sm py-8 text-center">No activity yet.</div>}
          {!loading && logs.length > 0 && (
            <div className="space-y-2">
              {logs.map((l) => {
                const editable = l.action === 'consume' || l.action === 'restock';
                const txnDate = l.transaction_date || l.created_at;
                return (
                  <div key={l.id} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid={`log-${l.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <span className={`text-xs font-medium uppercase tracking-wide ${
                          l.action === 'consume' ? 'text-orange-300' :
                          l.action === 'restock' ? 'text-emerald-300' :
                          l.action === 'create' ? 'text-cyan-300' : 'text-slate-300'
                        }`}>{l.action}</span>
                        <span className="ml-2 font-semibold">
                          {l.quantity_change > 0 ? '+' : ''}{l.quantity_change} {item.unit}
                        </span>
                        <span className="text-slate-400 text-xs ml-1">→ {l.quantity_after} {item.unit}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-xs text-slate-500 text-right">
                          <div>{new Date(txnDate).toLocaleDateString()}</div>
                          <div className="text-[10px] opacity-70">recorded {new Date(l.created_at).toLocaleString()}</div>
                        </div>
                        {editable && (
                          <>
                            <button
                              onClick={() => setEditingLog(l)}
                              className="text-xs bg-white/5 hover:bg-teal-500/20 border border-white/10 hover:border-teal-400/40 text-slate-300 hover:text-teal-200 px-2 py-1 rounded transition"
                              data-testid={`edit-log-${l.id}`}
                              title="Correct this entry"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={async () => {
                                const qty = Math.abs(Number(l.quantity_change));
                                if (!window.confirm(
                                  `Delete this ${l.action} entry of ${qty} ${item.unit}?\n\n` +
                                  `Current stock will be ${l.action === 'restock' ? 'reduced' : 'increased'} by ${qty} ${item.unit} to reverse this entry.`
                                )) return;
                                try {
                                  await axios.delete(`${API}/inventory-logs/${l.id}`, { headers: authHeader() });
                                  fetchLogs();
                                  onItemChanged && onItemChanged();
                                } catch (e) {
                                  notifyErr(e);
                                }
                              }}
                              className="text-xs bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-slate-300 hover:text-red-300 px-2 py-1 rounded transition"
                              data-testid={`delete-log-${l.id}`}
                              title="Delete this entry (reverses the stock change)"
                            >
                              <Trash2 size={11} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      by {l.user_email}
                      {l.car_model ? <> · <span className="text-cyan-300 font-medium">Car: {l.car_model}</span></> : null}
                      {l.notes ? ` · ${l.notes}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {editingLog && (
        <EditLogDialog
          log={editingLog}
          item={item}
          onClose={() => setEditingLog(null)}
          onSaved={() => {
            setEditingLog(null);
            fetchLogs();
            onItemChanged && onItemChanged();
          }}
          authHeader={authHeader}
        />
      )}
    </div>
  );
};

export default LogsDialog;
